import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [devCode, setDevCode] = useState(null);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDevCode(data.dev_code);
      setStep(2);
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Forgot password" : "Set a new password"}
      subtitle={step === 1
        ? "Enter your email and we will send a reset code."
        : `Enter the code sent to ${email} and choose a new password.`}
      footer={<Link to="/login" className="font-semibold text-primary hover:underline">Back to sign in</Link>}
    >
      {step === 1 ? (
        <form onSubmit={request} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="fp-email">Email address</Label>
            <Input id="fp-email" type="email" required value={email} data-testid="forgot-email" className="h-11"
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="forgot-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Send reset code
          </Button>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-5">
          {devCode && (
            <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning-soft p-3">
              <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p className="text-xs leading-relaxed">
                Email is not configured yet, so your reset code is <b className="num tracking-widest">{devCode}</b>.
              </p>
            </div>
          )}
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode}>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-base" />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <div className="space-y-2">
            <Label htmlFor="np">New password</Label>
            <Input id="np" type="password" required minLength={8} value={password} className="h-11"
              onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={busy || code.length < 6}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
